
import React, { useState } from 'react';
import { ERPModule, User, Country } from '../types.ts';
import { 
  LayoutDashboard, 
  Route, 
  Users, 
  Package, 
  FileText, 
  CheckSquare, 
  Anchor, 
  X,
  ShieldCheck,
  Settings,
  ChevronDown,
  Globe,
  ChevronLeft,
  ChevronRight,
  Lock,
  ShieldAlert
} from 'lucide-react';

interface SidebarProps {
  activeModule: ERPModule;
  activeCountry: Country;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onModuleChange: (module: ERPModule) => void;
  onCountryChange: (country: Country) => void;
  user: User;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  activeModule, 
  activeCountry, 
  isCollapsed,
  onToggleCollapse,
  onModuleChange, 
  onCountryChange,
  user, 
  onClose 
}) => {
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const isAdmin = user.role?.toLowerCase() === 'admin';
  
  // Logic: Users can switch branches if they are Admin OR have more than one authorized country
  const canSwitchBranch = isAdmin || (user.allowedCountries && user.allowedCountries.length > 1);
  const availableCountries = isAdmin ? Object.values(Country) : (user.allowedCountries || [user.homeCountry]);
  
  const isBranchLocked = activeModule === ERPModule.QUOTES;

  const menuItems = [
    { id: ERPModule.DASHBOARD, label: 'Command Center', icon: LayoutDashboard },
    { id: ERPModule.INFRASTRUCTURE, label: 'Projects', icon: Route },
    { id: ERPModule.ORDERS_PIPELINE, label: 'Orders Pipeline', icon: Package },
    { id: ERPModule.PORT_BIT_TRACKER, label: 'Import Database', icon: Anchor },
    { id: ERPModule.CRM, label: 'CRM', icon: Users },
    { id: ERPModule.QUOTES, label: 'Proforma', icon: FileText },
    { id: ERPModule.TASKS, label: 'Tasks', icon: CheckSquare },
    { id: ERPModule.USERS, label: 'Console', icon: ShieldCheck, adminOnly: true },
  ];

  const getCountryColor = (c: Country) => {
    switch(c) {
      case Country.TANZANIA: return 'bg-emerald-500';
      case Country.UGANDA: return 'bg-amber-500';
      case Country.KENYA: return 'bg-red-600';
      default: return 'bg-slate-600';
    }
  };

  return (
    <aside className={`h-full bg-slate-900 text-slate-300 flex flex-col shadow-2xl relative border-r border-slate-800 transition-all duration-300 ${isCollapsed ? 'w-20' : 'w-64'}`}>
      {/* Sidebar Header */}
      <div className={`p-6 border-b border-slate-800 flex items-center shrink-0 transition-all ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-black text-xl shadow-lg shadow-indigo-600/20 shrink-0">
            S
          </div>
          {!isCollapsed && (
            <div className="animate-in fade-in slide-in-from-left-2">
              <h1 className="text-white font-black tracking-tight text-sm uppercase leading-none">Nexus Hub</h1>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">Enterprise Core</p>
            </div>
          )}
        </div>
        {!isCollapsed && (
          <button onClick={onClose} className="lg:hidden p-2 text-slate-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        )}
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-1">
        {!isCollapsed && (
          <div className="px-4 mb-4 mt-2 animate-in fade-in">
              <span className="text-[10px] font-black text-slate-600 uppercase tracking-[0.2em]">Operational Stack</span>
          </div>
        )}
        
        {menuItems.map((item) => {
          if (item.adminOnly && !isAdmin) return null;
          
          const isActive = activeModule === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onModuleChange(item.id)}
              title={isCollapsed ? item.label : ''}
              className={`w-full flex items-center rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all group overflow-hidden ${
                isCollapsed ? 'justify-center p-3.5' : 'px-4 py-3.5 gap-3'
              } ${
                isActive 
                  ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-100/20' 
                  : 'hover:bg-slate-800/50 hover:text-white text-slate-400'
              }`}
            >
              <item.icon size={18} className={`${isActive ? 'text-white' : 'text-slate-500 group-hover:text-indigo-400'} transition-colors shrink-0`} />
              {!isCollapsed && <span className="truncate animate-in fade-in slide-in-from-left-2">{item.label}</span>}
              {isActive && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white animate-pulse"></div>
              )}
            </button>
          );
        })}
      </nav>

      {/* Sidebar Footer with interactive Branch Switcher */}
      <div className={`p-4 border-t border-slate-800 bg-slate-900/50 relative ${isCollapsed ? 'items-center' : ''}`}>
        {/* Country Selector Dropdown - Only accessible if user can switch and if not locked */}
        {canSwitchBranch && isCountryDropdownOpen && !isCollapsed && !isBranchLocked && (
          <div className="absolute bottom-full left-4 right-4 mb-3 bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 duration-200 z-50 ring-1 ring-black/50">
            <div className="p-3 border-b border-slate-700/50 bg-slate-900/30">
               <span className="text-[9px] font-black text-slate-500 uppercase tracking-[0.2em] px-2">Authorized Node Switch</span>
            </div>
            {availableCountries.map(c => (
              <button 
                key={c}
                onClick={() => {
                  onCountryChange(c);
                  setIsCountryDropdownOpen(false);
                }}
                className={`w-full text-left px-5 py-3 text-[10px] font-black uppercase tracking-widest hover:bg-slate-700 flex items-center justify-between transition-colors group ${activeCountry === c ? 'text-indigo-400 bg-slate-700/50' : 'text-slate-400'}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full transition-transform group-hover:scale-125 ${getCountryColor(c)}`}></div>
                  {c}
                </div>
                {activeCountry === c && <div className="w-1 h-1 rounded-full bg-indigo-400 animate-pulse"></div>}
              </button>
            ))}
          </div>
        )}

        {isAdmin && !isCollapsed && (
           <div className="mb-4 px-4 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-xl flex items-center gap-2">
             <ShieldAlert size={12} className="text-indigo-400" />
             <span className="text-[8px] font-black uppercase tracking-widest text-indigo-300">Admin Authority Root</span>
           </div>
        )}

        <button 
          onClick={() => canSwitchBranch && !isCollapsed && !isBranchLocked && setIsCountryDropdownOpen(!isCountryDropdownOpen)}
          disabled={!canSwitchBranch || isBranchLocked}
          title={isCollapsed ? `Branch: ${activeCountry}` : isBranchLocked ? 'Financial Registry Locked' : canSwitchBranch ? 'Click to switch branch' : 'Access Restricted'}
          className={`w-full bg-slate-800/40 rounded-2xl flex items-center border transition-all text-left group overflow-hidden ${
            isCollapsed ? 'justify-center p-2 border-transparent' : 'p-4 gap-3 border-slate-800/50'
          } ${canSwitchBranch && !isCollapsed && !isBranchLocked ? 'hover:border-slate-700 cursor-pointer' : 'cursor-default'} ${isCountryDropdownOpen ? 'border-indigo-500 bg-slate-800/60 shadow-lg shadow-indigo-500/5' : ''} ${isBranchLocked ? 'opacity-75 grayscale-[0.5]' : ''}`}
        >
           <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-[10px] shadow-sm transition-transform group-hover:scale-110 shrink-0 ${getCountryColor(activeCountry)} text-white`}>
             {isBranchLocked && !isCollapsed ? <Lock size={12} /> : activeCountry.substring(0, 2)}
           </div>
           {!isCollapsed && (
             <div className="flex-1 min-w-0 animate-in fade-in slide-in-from-left-2">
                <p className="text-[9px] font-black text-slate-600 uppercase tracking-widest flex items-center gap-1.5">
                  {( !canSwitchBranch || isBranchLocked ) && <Lock size={8} className="text-slate-500" />}
                  {isBranchLocked ? 'Registry Locked' : 'Active Node'}
                </p>
                <p className="text-xs font-bold text-slate-300 truncate">{activeCountry}</p>
             </div>
           )}
           {canSwitchBranch && !isCollapsed && !isBranchLocked && <ChevronDown size={14} className={`text-slate-500 transition-transform duration-300 ${isCountryDropdownOpen ? 'rotate-180' : ''}`} />}
        </button>

        <div className={`mt-4 flex items-center gap-2 text-slate-600 group cursor-pointer hover:text-slate-400 transition-colors ${isCollapsed ? 'justify-center' : 'justify-center'}`}>
            <Settings size={12} className="shrink-0" />
            {!isCollapsed && <span className="text-[9px] font-black uppercase tracking-widest animate-in fade-in slide-in-from-left-2">System Settings</span>}
        </div>

        {/* Minimize Toggle Button */}
        <button 
          onClick={onToggleCollapse}
          className="mt-4 w-full flex items-center justify-center p-3 text-slate-500 hover:text-indigo-400 hover:bg-slate-800/50 rounded-2xl transition-all group"
        >
          {isCollapsed ? <ChevronRight size={18} /> : (
            <div className="flex items-center gap-2">
              <ChevronLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
              <span className="text-[9px] font-black uppercase tracking-widest">Minimize</span>
            </div>
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
